import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { Hospital } from 'types/hospital';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';

export type HospitalQueryParams = {
  active: boolean;
  company_name: string;
  document_number: string;
};

export function useFetchHospitals() {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  const [filter, setFilter] = useState<HospitalQueryParams>({
    company_name: '',
    active: true,
    document_number: '',
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);

    api
      .get<Paginated<Hospital[]>>('/hospitals', {
        params: {
          ...nextFilter,
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _hospitals = response.data.items.map(hospital => ({
          ...hospital,
          formattedActive: hospital.active ? 'Sim' : 'Não',
          formattedState: hospital.address?.state || '',
          formattedStreet: hospital.address?.street || '',
          formattedCity: hospital.address?.city || '',
          formattedNeighborhood: hospital.address?.neighborhood || '',
        }));
        setHospitals(_hospitals);
      })
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, hospitals);
    setHospitals(p);
  }

  function handleChange(index: keyof HospitalQueryParams, value: string) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    hospitals,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
  };
}
